/**
 * `contacts` — people around the gig: promoter, venue staff, production, drivers.
 * NOT the touring crew's own profiles — that is the crew collection (P3).
 *
 * `role` demonstrates the open-enum pattern of ARCHITECTURE.md §3.1: canonical
 * stored values + suggested chips, while legacy free text still validates.
 * `notes` is deliberately UNTAGGED — default-deny keeps it owner/editor-only.
 */
import { z } from "zod";
import { defineCollection, field } from "../define.ts";

export const contacts = defineCollection({
  key: "contacts", // the ONLY place this string is born (CONSTRAINTS #5)
  labelKey: "collections.contacts",
  icon: "user",
  sheetTab: "Contacts",
  dedupFields: ["name", "phone"], // -> op dedup_key (§4.1)
  sort: [{ field: "name" }],
  split: "people", // NOTE: canonical 4-split names are pinned by the P3 UI work
  chatHint:
    "People around the gig: promoter, venue staff, production manager, drivers, press. " +
    "One row per person with how to reach them. The touring crew's own profiles are NOT " +
    "contacts — they belong in the crew collection (ships in P3).",
  fields: {
    name: field({
      type: z.string().min(1),
      tier: "hot",
      pii: "crew",
      labelKey: "contacts.name",
      sheet: { column: "Name" },
      description: "The person's name as the crew knows them.",
    }),
    role: field({
      type: z.string().optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "contacts.role",
      sheet: { column: "Role" },
      enum: {
        // Canonical stored values (feed chips, Sheet dropdown, chat tools).
        // open: legacy free text ("stage mgr / lights") still validates (§3.1).
        values: ["promoter", "venue", "production", "press", "driver", "other"],
        open: true,
      },
      suggestedChips: ["promoter", "venue", "production", "driver"],
      description: "What this person does for the gig: promoter, venue, production, press, driver…",
    }),
    phone: field({
      type: z.string().optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "contacts.phone",
      input: "tel",
      sheet: { column: "Phone" },
      description: "Phone / WhatsApp number — what the crew dials on the road.",
    }),
    email: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "contacts.email",
      input: "email",
      sheet: { column: "Email" },
      description: "Email address. Kept as free text — legacy data is messy.",
    }),
    org: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "contacts.org",
      sheet: { column: "Organisation" },
      description: "Company / venue / agency the person belongs to.",
    }),
    notes: field({
      type: z.string().optional(),
      tier: "detail",
      // NO pii tag on purpose: default-deny makes this owner/editor-only.
      // Working notes (fee talk, impressions) must never reach crew/share scopes.
      labelKey: "contacts.notes",
      sheet: "extras", // routed to the single explicit extras JSON column (§3.1)
      description: "Owner/editor-only working notes: negotiation state, impressions, history.",
    }),
  },
});
